import { NgClass } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TransformTitlePipe } from 'app/core/pipes/transform-title.pipe';
import { AuthService } from '../../../../auth/auth.service';

@Component({
  selector: 'app-activity-timeline',
  standalone: true,
  imports: [NgClass, MatIconModule, MatProgressSpinnerModule, TransformTitlePipe],
  templateUrl: './activity-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityTimelineComponent implements OnInit {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() limit = 5;
  @Input() filters: any = {};
  @Input() requiredPermission?: string;

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  activities: any[] = [];
  loading = false;
  error: string | null = null;

  ngOnInit(): void {
    if (!this.hasPermission) return;
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.http
      .get('api/activity', { params: { limit: String(this.limit) } })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const data = res.record?.data ?? res.record ?? [];
          this.activities = Array.isArray(data) ? data : [];
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.status === 403 ? 'Access restricted' : err.status === 500 ? 'Service unavailable' : 'Unable to load data';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    return dateStr.replace('T', ' ').slice(0, 16);
  }

  /**
   * Readable name for a log's subject. Models without a backend morph alias arrive as a class path
   * (e.g. "App\Models\Inspections\InspectionSnag"); show a label instead of the path.
   */
  subjectLabel(subjectType: string): string {
    if (!subjectType) return '';
    const parts = subjectType.split('\\');
    const name = parts[parts.length - 1];
    if (parts.includes('Inspections')) {
      const inspectionLabels: Record<string, string> = {
        Inspection: 'Inspection Report',
        InspectionSnag: 'Snag',
        InspectionSnagCategory: 'Snag Category',
        InspectionSnagComment: 'Snag Comment',
        InspectionSnagCommentAttachment: 'Snag Comment Attachment',
        InspectionSnagImage: 'Snag Image',
        InspectionSnagFloor: 'Snag Floor',
        InspectionSnagAsset: 'Snag Asset',
        Inspector: 'Inspector',
        InspectionRole: 'Inspection Role',
        InspectionPermission: 'Inspection Permission',
        InspectionCategory: 'Inspection Category',
        InspectionItem: 'Inspection Item',
        InspectionItemImage: 'Inspection Item Image',
        InspectionLog: 'Inspection Log',
        Category: 'Checklist Category',
        Subcategory: 'Checklist Subcategory',
        Item: 'Checklist Item',
      };
      if (inspectionLabels[name]) return inspectionLabels[name];
    }
    // "InspectionSnag" / "bank_account" / "role" -> "Inspection Snag" / "Bank Account" / "Role"
    return name
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  getActionIcon(action: string): string {
    const map: Record<string, string> = {
      created: 'add_circle',
      updated: 'edit',
      deleted: 'delete',
      approved: 'check_circle',
      rejected: 'cancel',
      exported: 'download',
      login: 'login',
    };
    return map[action?.toLowerCase()] ?? 'circle';
  }
}
